import { z } from "zod";
import { IDENTITY_PROVIDERS, MODULES, PERMISSION_LEVELS, USER_TYPES } from "../enums.ts";

export const PasswordLoginInput = z.object({
  email: z.string().email().max(200),
  password: z.string().min(1).max(200),
});
export type PasswordLoginInput = z.infer<typeof PasswordLoginInput>;

/** Simulated OIDC assertion used by the local mock providers. */
export const MockSsoAssertion = z.object({
  provider: z.enum(IDENTITY_PROVIDERS),
  subject: z.string().min(1).max(200),
  email: z.string().email().max(200),
  emailVerified: z.boolean().default(true),
  name: z.string().max(200).optional(),
});
export type MockSsoAssertion = z.infer<typeof MockSsoAssertion>;

export const InviteInput = z.object({
  email: z.string().email().max(200),
  name: z.string().min(1).max(200),
  userType: z.enum(USER_TYPES),
  roleId: z.string().uuid(),
  companyId: z.string().uuid().nullable().optional(),
  personId: z.string().uuid().nullable().optional(),
  locationType: z.enum(["facility", "plant"]).nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
});
export type InviteInput = z.infer<typeof InviteInput>;

export const AcceptInviteInput = z.object({
  token: z.string().min(10),
  name: z.string().min(1).max(200).optional(),
  password: z.string().min(10, "Use at least 10 characters").max(200).optional(),
});
export type AcceptInviteInput = z.infer<typeof AcceptInviteInput>;

export const ChangePasswordInput = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(10).max(200),
});

export const RoleInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/, "lowercase letters, digits and underscores"),
  name: z.string().min(1).max(100),
  userType: z.enum(USER_TYPES),
  permissions: z.record(z.enum(MODULES), z.enum(PERMISSION_LEVELS)).default({}),
});
export type RoleInput = z.infer<typeof RoleInput>;

export const RoleUpdateInput = z.object({
  name: z.string().min(1).max(100).optional(),
  permissions: z.record(z.enum(MODULES), z.enum(PERMISSION_LEVELS)).optional(),
});

export const UserUpdateInput = z.object({
  name: z.string().min(1).max(200).optional(),
  roleId: z.string().uuid().optional(),
  active: z.boolean().optional(),
  personId: z.string().uuid().nullable().optional(),
});

export const SessionPolicy = z.object({
  absoluteHours: z.number().int().min(1).max(24 * 90),
  idleMinutes: z.number().int().min(0).max(24 * 60 * 90).describe("0 = no idle timeout"),
});
export type SessionPolicy = z.infer<typeof SessionPolicy>;

export const Me = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  userType: z.enum(USER_TYPES),
  role: z.object({ id: z.string().uuid(), key: z.string(), name: z.string() }),
  permissions: z.record(z.string(), z.enum(PERMISSION_LEVELS)),
  companyId: z.string().uuid().nullable(),
  companyName: z.string().nullable(),
  personId: z.string().uuid().nullable(),
  locationType: z.enum(["facility", "plant"]).nullable(),
  locationId: z.string().uuid().nullable(),
  identities: z.array(z.object({ provider: z.enum(IDENTITY_PROVIDERS), email: z.string() })),
  hasPassword: z.boolean(),
  sessionExpiresAt: z.string(),
});
export type Me = z.infer<typeof Me>;
